import { Box, InputAdornment, TableCell, TableRow } from "@mui/material"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { useGetAdjustmentLotProductTest } from "../../hooks"
import type { LotCorrectionAdjustmentFormValues } from "../../schemas"
import type { AdjustmentSelectedLot } from "../../types"
import { AdjustmentDuplicateLotError } from "../AdjustmentDuplicateLotError"
import { AdjustmentLotDeleteNotice } from "../AdjustmentLotDeleteNotice"
import { AdjustmentQuantityDiff } from "../AdjustmentQuantityDiff"

import { FormNumericField, FormTextField } from "@/components/UiKit"
import { WarningMessage } from "@/components/WarningMessage/WarningMessage"
import { type Product, type ProductPackagingType } from "@/graphql/codegen/graphql"
import { getUntestedLotMessage } from "@/utils/lot"
import { formatNumber } from "@/utils/number"
import { calculateRemainingShelfLife, getProductPackagingTypeLabel } from "@/utils/product"
import { isNil } from "@/utils/utils"

interface LotCorrectionAdjustmentExistingLotRowProps {
  product: Product
  uomName: string
  index: number
  lot: AdjustmentSelectedLot
  isDuplicate: boolean
}

export const LotCorrectionAdjustmentExistingLotRow: FC<LotCorrectionAdjustmentExistingLotRowProps> = ({
  index,
  product,
  uomName,
  lot,
  isDuplicate,
}) => {
  const { control, watch } = useFormContext<LotCorrectionAdjustmentFormValues>()

  const baseFieldPath = `existingLots.${index}` as const
  const fieldPaths = {
    lotNumber: `${baseFieldPath}.lotNumber` as const,
    batchNumber: `${baseFieldPath}.batchNumber` as const,
    quantity: `${baseFieldPath}.quantity` as const,
  }

  const [lotNumber, batchNumber, quantity] = watch([fieldPaths.lotNumber, fieldPaths.batchNumber, fieldPaths.quantity])

  const { shouldShowUntestedLotWarning } = useGetAdjustmentLotProductTest({
    productId: product.productId,
    lotNumber,
    batchNumber,
  })

  const remainingShelfLife = calculateRemainingShelfLife(lot.manufacturingDate, lot.expirationDate)

  return (
    <TableRow
      className='align-top'
      data-testid={`inventory.lot-correction-adjustment-existing-lot-row-${lot.rawInventoryId}`}
    >
      <TableCell className='pt-6'>{lot.pallet.referenceNbr}</TableCell>
      <TableCell className='pt-6'>{lot.pallet.location}</TableCell>
      <TableCell>
        <Box className='flex flex-1 flex-col gap-y-2'>
          <FormTextField
            control={control}
            name={fieldPaths.lotNumber}
            required
            data-testid={`inventory.lot-correction-adjustment-existing-lot-row.lot-number-field-${index}`}
          />
          {shouldShowUntestedLotWarning && (
            <WarningMessage
              message={getUntestedLotMessage(batchNumber)}
              data-testid={`inventory.lot-correction-adjustment-existing-lot-row.untested-lot-warning-${index}`}
            />
          )}
          {isDuplicate && (
            <AdjustmentDuplicateLotError
              data-testid={`inventory.lot-correction-adjustment-existing-lot-row.duplicate-lot-error-${index}`}
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        <FormTextField
          control={control}
          name={fieldPaths.batchNumber}
          data-testid={`inventory.lot-correction-adjustment-existing-lot-row.batch-number-field-${index}`}
        />
      </TableCell>
      <TableCell className='whitespace-nowrap pt-6'>
        {isNil(remainingShelfLife) ? "" : `${remainingShelfLife} %`}
      </TableCell>
      <TableCell className='pt-6'>{getProductPackagingTypeLabel(lot.packageDesc as ProductPackagingType)}</TableCell>
      <TableCell className='pt-6'>{lot.totalPackages}</TableCell>
      <TableCell className='whitespace-nowrap pt-6'>
        {isNil(lot.packageSize) ? "" : `${formatNumber(lot.packageSize)} ${uomName}`}
      </TableCell>
      <TableCell className='whitespace-nowrap pt-6'>
        {isNil(lot.totalUnits) ? "" : `${formatNumber(lot.totalUnits)} ${uomName}`}
      </TableCell>
      <TableCell>
        <FormNumericField
          control={control}
          name={fieldPaths.quantity}
          required
          allowNegative={false}
          decimalScale={2}
          slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> } }}
          className='w-40'
          helperText={quantity === 0 ? <AdjustmentLotDeleteNotice /> : null}
          data-testid={`inventory.lot-correction-adjustment-existing-lot-row.quantity-field-${index}`}
        />
      </TableCell>
      <TableCell className='whitespace-nowrap pt-6'>
        {isNil(quantity) || isNil(lot.totalUnits) ? null : (
          <AdjustmentQuantityDiff quantity={quantity} originalQuantity={lot.totalUnits} uomName={uomName} />
        )}
      </TableCell>
      <TableCell />
    </TableRow>
  )
}
